import { Link, useNavigate, useLocation } from "react-router-dom";
import "../styles/navbar.css";
import logo from "../assets/images/Logo.png";
import rename_icon from "../assets/images/rename.png";
import userIcon from "../assets/images/userProfile.png";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { API_CONFIG } from "../config/api";
import { generateAuthToken, generateURN } from "../components/hmac";
import Alerts from "../components/Alerts"

const Navbar = () => {
  const { user, isAdmin, profileUser, setProfileUser } = useAuth();
  const [hidden, setHidden] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showProfile, setShowProfile] = useState(false);
  const navigate = useNavigate();
  const profileRef = useRef(null);
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [newName, setNewName] = useState("");


  const handleLoginRedirect = () => navigate("/login");

  const { host, logout } = API_CONFIG;

  const handleLogout = () => {
    window.location.href = `https://${host}${logout.path}`;
  };

  /* ------------------------------
     AUTO FETCH PROFILE ON LOGIN
     (needed for puzzle tick marks)
  ------------------------------ */
  useEffect(() => {
    if (!user?.userEmail || profileUser) return;

    const fetchProfile = async () => {
      try {
        const { host, fetchUserInfo, basic } = API_CONFIG;
        const urn = generateURN();
        const authToken = generateAuthToken(urn);

        const res = await fetch(
          `https://${host}${fetchUserInfo.path}`,
          {
            method: fetchUserInfo.method,
            headers: {
              "Content-Type": "application/json",
              useremail: user.userEmail,
              Authorization: `Basic ${basic.BASIC_AUTH_CWUSER}`,
              urn,
              authToken,
            },
            credentials: "include",
          }
        );

        const data = await res.json();
        const userData = data?.apiResponseData?.responseData;

        if (userData) {
          setProfileUser(userData); // ✅ GLOBAL STORE
        }
      } catch (err) {
        console.error("Auto profile fetch failed", err);
      }
    };

    fetchProfile();
  }, [user, profileUser, setProfileUser]);

  /* ------------------------------
     PROFILE CLICK → REFRESH DATA
  ------------------------------ */
  const handleProfileClick = async () => {
    const nextState = !showProfile;
    setShowProfile(nextState);

    if (nextState && user?.userEmail) {
      try {
        const { host, fetchUserInfo, basic } = API_CONFIG;
        const urn = generateURN();
        const authToken = generateAuthToken(urn);

        const res = await fetch(
          `https://${host}${fetchUserInfo.path}`,
          {
            method: fetchUserInfo.method,
            headers: {
              "Content-Type": "application/json",
              useremail: user.userEmail,
              Authorization: `Basic ${basic.BASIC_AUTH_CWUSER}`,
              urn,
              authToken,
            },
            credentials: "include",
          }
        );

        const data = await res.json();
        const userData = data?.apiResponseData?.responseData;

        if (userData) {
          setProfileUser(userData); // ✅ REFRESH GLOBAL STORE
        }
      } catch (err) {
        console.error("Failed to fetch profile data", err);
      }
    }
  };

  /* ------------------------------
     CLOSE DROPDOWN ON OUTSIDE CLICK
  ------------------------------ */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setShowProfile(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ------------------------------
     NAVBAR SCROLL HIDE
  ------------------------------ */
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 80) {
        setHidden(true);
      } else {
        setHidden(false);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    const handleScroll = () => setMenuOpen(false);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const changeUserName = async (userName) => {

  try {
    const { host, basic,updateName } = API_CONFIG;
    const urn = generateURN();
    const authToken = generateAuthToken(urn);
    const response=await fetch(`https://${host}${updateName.path}`,{
      method: updateName.method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Basic ${basic.BASIC_AUTH_CWUSER}`,
        urn,
        authToken,
        userEmail: user.userEmail,
      },
      credentials: "include",
      body: JSON.stringify({userName: userName}),
    });
     const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.apiResponseMessage || "Update failed");
      }

  } catch (err) {
    console.error("Solve API failed", err);
  }
};

  const isActive = (path) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  return (
    <header className={`navbar ${hidden ? "navbar-hidden" : ""}`}>
      <button
        className="hamburger-btn"
        onClick={() => setMenuOpen(!menuOpen)}
      >
        ☰
      </button>

      <div className="logo_container">
        <img src={logo} alt="ChessWizzania" className="logo" />
      </div>

      <nav className="desktop-nav">
        <ul className="nav-links">
          <li className={isActive("/") ? "active" : ""}>
            <Link to="/">Home</Link>
          </li>
          <li className={isActive("/courses") ? "active" : ""}>
            <Link to="/courses">Course</Link>
          </li>
          <li className={isActive("/tournaments") ? "active" : ""}>
            <Link to="/tournaments">Tournament</Link>
          </li>
          <li className={isActive("/about") ? "active" : ""}>
            <Link to="/about">About</Link>
          </li>
          <li className={isActive("/ranking") ? "active" : ""}>
            <Link to="/ranking">Ranking</Link>
          </li>

          {isAdmin && (
            <li
              className={`admin-link ${
                isActive("/admin") ? "active" : ""
              }`}
            >
              <Link to="/admin">Admin</Link>
            </li>
          )}
        </ul>
      </nav>
      	
      {/* MOBILE DRAWER */}
      <div className={`mobile-drawer ${menuOpen ? "open" : ""}`}>
        <button className="close-btn" onClick={() => setMenuOpen(false)}>✕</button>
        <Link to="/" onClick={() => setMenuOpen(false)}>Home</Link>
        <Link to="/courses" onClick={() => setMenuOpen(false)}>Course</Link>
        <Link to="/tournaments" onClick={() => setMenuOpen(false)}>Tournament</Link>
        <Link to="/about" onClick={() => setMenuOpen(false)}>About</Link>
        <Link to="/ranking" onClick={() => setMenuOpen(false)}>Ranking</Link>
        {/* 👑 ADMIN LINK – MOBILE */}
        {isAdmin && (
          <Link to="/admin" onClick={() => setMenuOpen(false)}>
            Admin
          </Link>
        )}
      </div>
      {/* OVERLAY */}
      {menuOpen && (
        <div
          className="drawer-overlay"
          onClick={() => setMenuOpen(false)}
        />
      )}
      <div className="nav-action" ref={profileRef}>
        {!user && (
          <button className="signin-btn" onClick={handleLoginRedirect}>
            Sign in
          </button>
        )}

       
        {user &&(
        <Alerts/>
        )}


        {user && (
          <>
            <img
              src={userIcon}
              alt="Profile"
              className="user-profile-icon"
              onClick={handleProfileClick}
            />

            {showProfile && profileUser && (
              <div className="profile-dropdown">
                <div className="profile-row username">
                  {editing ? (
                    <>
                      <input
                        className="userName-input"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                      />
                      <button className="userName-btn" onClick={() => {
                        changeUserName(newName);
                        setEditing(false);
                        handleProfileClick();
                      }}>
                        Save
                      </button>
                    </>
                  ) : (
                    <>
                      <img
                        src={rename_icon} 
                        alt="rename"
                        className="rename"
                        onClick={() => setEditing(true)}
                      />
                      {profileUser.userName}
                    </>
                  )}
                </div>
                <div className="profile-row points">
                  🪙 Gold Points:{" "}
                  <strong>{profileUser.goldPoints}</strong>
                </div>

                <div className="profile-divider" />

                <div
                  className="profile-row logout"
                  onClick={handleLogout}
                >
                  Logout
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </header>
  );
};

export default Navbar;
